import React from "react";

/**
 * High-precision vector & crest lockup for NS&I (National Savings & Investments) | HM Treasury Backed (UK)
 * Matches the sovereign brushed-gold and slate-petrol typography and emblem layout of the NS&I Privilege Card.
 */
export function BdoLogoMark({ variant = "dark", className = "h-6 w-auto", showUnibank = true }) {
  const goldPrimary = variant === "light" ? "#142B39" : "#E5CA87";
  const goldAccent = variant === "light" ? "#8E6D2F" : "#C5A059";
  const subTextFill = variant === "light" ? "#1E3A4C" : "rgba(229,202,135,0.88)";

  if (!showUnibank) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 155 56"
        fill="none"
        className={className}
        aria-label="NS&I"
      >
        <defs>
          <linearGradient id="nsiGoldGradShort" x1="0" y1="0" x2="155" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={goldPrimary} />
            <stop offset="50%" stopColor={variant === "light" ? "#1F3F52" : "#F5E2B3"} />
            <stop offset="100%" stopColor={goldAccent} />
          </linearGradient>
        </defs>
        <text
          x="2"
          y="43"
          fill="url(#nsiGoldGradShort)"
          fontFamily="Georgia, 'Times New Roman', serif"
          fontWeight="700"
          fontSize="44"
          letterSpacing="1.5"
        >
          NS&amp;I
        </text>
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 285 68"
      fill="none"
      className={className}
      aria-label="NS&I National Savings and Investments"
    >
      <defs>
        <linearGradient id="nsiGoldGradFull" x1="0" y1="0" x2="260" y2="68" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={goldPrimary} />
          <stop offset="50%" stopColor={variant === "light" ? "#1F3F52" : "#F5E2B3"} />
          <stop offset="100%" stopColor={goldAccent} />
        </linearGradient>
      </defs>
      <text
        x="2"
        y="46"
        fill="url(#nsiGoldGradFull)"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontWeight="700"
        fontSize="46"
        letterSpacing="1.5"
      >
        NS&amp;I
      </text>
      <line
        x1="138"
        y1="12"
        x2="138"
        y2="52"
        stroke={goldAccent}
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      <text
        x="148"
        y="28"
        fill={subTextFill}
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontSize="13.5"
        fontWeight="600"
        letterSpacing="0.4"
      >
        National Savings
      </text>
      <text
        x="148"
        y="46"
        fill={subTextFill}
        fontFamily="'Inter', 'Helvetica Neue', Arial, sans-serif"
        fontSize="13.5"
        fontWeight="600"
        letterSpacing="0.4"
      >
        and Investments
      </text>
    </svg>
  );
}

export function PalLogoMark({ variant = "dark", className = "h-7 w-auto" }) {
  const titleFill = variant === "light" ? "#142B39" : "#E5CA87";
  const subFill = variant === "light" ? "#8E6D2F" : "rgba(255,255,255,0.78)";
  const crownStroke = variant === "light" ? "#8E6D2F" : "#D4B56A";

  return (
    <div className={`inline-flex items-center gap-2 select-none ${className}`}>
      {/* HM Treasury Royal Crown Emblem */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 48 44"
        fill="none"
        className="h-full w-auto shrink-0 drop-shadow-sm"
        aria-hidden="true"
      >
        <circle cx="24" cy="22" r="20" stroke={crownStroke} strokeOpacity="0.45" strokeWidth="1.3" fill={variant === "light" ? "rgba(20,43,57,0.05)" : "rgba(212,181,106,0.08)"} />
        {/* St Edward's Crown stylized silhouette */}
        <path
          d="M13 28L11 17L18 21L24 13L30 21L37 17L35 28H13Z"
          fill={crownStroke}
          fillOpacity="0.25"
          stroke={crownStroke}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
        <path d="M12 31H36" stroke={crownStroke} strokeWidth="2" strokeLinecap="round" />
        <circle cx="24" cy="10" r="1.6" fill={crownStroke} />
        <circle cx="11" cy="14.5" r="1.4" fill={crownStroke} />
        <circle cx="37" cy="14.5" r="1.4" fill={crownStroke} />
      </svg>
      <div
        className="flex flex-col justify-center leading-[1.08] font-semibold tracking-tight"
        style={{ color: titleFill }}
      >
        <span className="text-[11px] sm:text-[12px] uppercase tracking-[0.12em] font-bold">HM Treasury</span>
        <span className="text-[10px] sm:text-[11px] tracking-[0.06em]" style={{ color: subFill }}>
          100% Backed • UK
        </span>
      </div>
    </div>
  );
}

export default function BrandLockup({
  variant = "dark",
  size = "md",
  showBadge = false,
  badgeText = "HM Treasury Backed",
  className = "",
}) {
  const sizes = {
    sm: {
      bdo: "h-6 w-auto",
      divider: "h-5",
      pal: "h-6",
      gap: "gap-2.5",
    },
    md: {
      bdo: "h-7 sm:h-8 w-auto",
      divider: "h-6 sm:h-7",
      pal: "h-6 sm:h-7",
      gap: "gap-3 sm:gap-3.5",
    },
    lg: {
      bdo: "h-8 sm:h-9 w-auto",
      divider: "h-7 sm:h-8",
      pal: "h-7 sm:h-8",
      gap: "gap-3.5 sm:gap-4",
    },
  };

  const s = sizes[size] || sizes.md;
  const dividerColor =
    variant === "light" ? "bg-slate-300" : "bg-gradient-to-b from-white/10 via-[#D4B56A]/60 to-white/10";

  return (
    <div className={`inline-flex items-center ${s.gap} select-none ${className}`}>
      <BdoLogoMark variant={variant} className={s.bdo} showUnibank={true} />
      <span className={`w-px ${s.divider} ${dividerColor} shrink-0`} aria-hidden="true" />
      <PalLogoMark variant={variant} className={s.pal} />
      {showBadge && (
        <span
          className={`hidden lg:inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
            variant === "light"
              ? "text-[#142B39] bg-[#142B39]/5 border-[#142B39]/20"
              : "text-[#E5CA87] bg-white/[0.04] border-[#D4B56A]/35"
          } ml-1`}
        >
          {badgeText}
        </span>
      )}
    </div>
  );
}
